/**
 * VaultDocumentSection — uniform per-tab content shell.
 *
 * Composes: upload widget (drag-and-drop), document list, per-row fly-out
 * detail sheet, delete-with-confirm. Used by every vault tab that holds
 * documents; structured-form tabs (Acknowledgements, Conduct) compose their
 * own forms instead.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Upload, FileText, Trash2, Download, X, FileBadge2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  uploaded: 'bg-slate-100 text-slate-700 border-slate-200',
  pending:  'bg-amber-50  text-amber-700  border-amber-200',
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50    text-rose-700    border-rose-200',
};

function humanSize(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function VaultDocumentSection({
  sectionId,
  acceptedTypes = '.pdf,.png,.jpg,.jpeg,.heic,.webp,.doc,.docx',
  uploadHint = 'Drop a file here or click to browse. PDF, image, or Word doc up to 25 MB.',
  emptyState = 'No documents uploaded yet for this section.',
  guidance,
  onChange,
}) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [openDoc, setOpenDoc] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/candidate/vault/documents`, { params: { section: sectionId } });
      setDocs(data || []);
    } catch (e) {
      setError('Could not load documents');
    } finally {
      setLoading(false);
    }
  }, [sectionId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    setError('');
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setError('File too large — 25 MB max.');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('section', sectionId);
      form.append('label', file.name);
      await axios.post(`${API}/candidate/vault/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
      if (onChange) onChange();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.label}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/candidate/vault/documents/${doc.id}`);
      setOpenDoc(null);
      await load();
      if (onChange) onChange();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Delete failed.');
    }
  };

  const downloadUrl = (doc) => `${API}/candidate/vault/documents/${doc.id}/download`;

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  return (
    <div className="space-y-4" data-testid={`vault-section-${sectionId}`}>
      {guidance && (
        <div className="bg-[var(--vh-bg-muted)] border border-[var(--vh-border)] rounded-2xl p-4 text-sm text-[var(--vh-text-secondary)]" data-testid={`vault-guidance-${sectionId}`}>
          {guidance}
        </div>
      )}

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className="border-2 border-dashed border-[var(--vh-border)] rounded-2xl p-6 text-center hover:bg-[var(--vh-bg-muted)] transition-colors"
        data-testid={`vault-dropzone-${sectionId}`}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={acceptedTypes}
          onChange={(e) => upload(e.target.files?.[0])}
          aria-label={`Upload document for ${sectionId}`}
          data-testid={`vault-upload-input-${sectionId}`}
        />
        <Upload className="w-7 h-7 mx-auto text-[var(--vh-text-muted)]" aria-hidden />
        <p className="mt-2 text-sm text-[var(--vh-text-secondary)]">{uploadHint}</p>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white text-sm font-medium rounded-full disabled:opacity-60"
          data-testid={`vault-upload-btn-${sectionId}`}
        >
          {uploading ? 'Uploading…' : 'Choose file'}
        </button>
      </div>

      {error && <p className="text-sm text-[var(--vh-accent)]" data-testid={`vault-error-${sectionId}`}>{error}</p>}

      <div>
        <h4 className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--vh-text-muted)] mb-2">
          Uploaded
        </h4>
        {loading ? (
          <p className="text-sm text-[var(--vh-text-muted)]" data-testid={`vault-loading-${sectionId}`}>Loading…</p>
        ) : docs.length === 0 ? (
          <p className="text-sm text-[var(--vh-text-muted)] py-4" data-testid={`vault-empty-${sectionId}`}>{emptyState}</p>
        ) : (
          <ul className="space-y-2" data-testid={`vault-list-${sectionId}`}>
            {docs.map((d) => (
              <li key={d.id} data-testid={`vault-doc-${d.id}`}
                  className="flex items-center gap-3 px-3 py-2.5 bg-white border border-[var(--vh-border)] rounded-xl hover:border-[var(--vh-brand-primary)] transition-colors">
                <div className="w-9 h-9 rounded-lg bg-[var(--vh-bg-muted)] text-[var(--vh-brand-primary)] flex items-center justify-center shrink-0" aria-hidden>
                  <FileText className="w-4 h-4" />
                </div>
                <button onClick={() => setOpenDoc(d)} className="flex-1 min-w-0 text-left" data-testid={`vault-doc-open-${d.id}`}>
                  <p className="text-sm font-medium truncate">{d.label}</p>
                  <p className="text-xs text-[var(--vh-text-muted)] mt-0.5">
                    {humanSize(d.size_bytes)} · {d.uploaded_at?.slice(0, 10)}
                  </p>
                </button>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_TONE[d.status] || STATUS_TONE.uploaded}`}>
                  {d.status}
                </span>
                <a href={downloadUrl(d)} target="_blank" rel="noopener noreferrer"
                   aria-label={`Download ${d.label}`}
                   className="vh-focusable w-8 h-8 rounded-full hover:bg-[var(--vh-bg-muted)] text-[var(--vh-text-muted)] flex items-center justify-center"
                   data-testid={`vault-doc-download-${d.id}`}>
                  <Download className="w-4 h-4" />
                </a>
                <button onClick={() => remove(d)} aria-label={`Delete ${d.label}`}
                        className="vh-focusable w-8 h-8 rounded-full hover:bg-rose-50 text-[var(--vh-accent)] flex items-center justify-center"
                        data-testid={`vault-doc-delete-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Detail fly-out sheet */}
      {openDoc && (
        <>
          <button
            aria-label="Close detail"
            tabIndex={-1}
            onClick={() => setOpenDoc(null)}
            className="fixed inset-0 bg-black/30 z-40 cursor-default"
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="vault-doc-detail-title"
            data-testid="vault-doc-flyout"
            className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white border-l border-[var(--vh-border)] z-50 shadow-[0_8px_32px_rgba(30,63,51,0.12)] flex flex-col"
          >
            <header className="px-5 py-4 border-b border-[var(--vh-border)] flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <FileBadge2 className="w-5 h-5 text-[var(--vh-brand-primary)] shrink-0" aria-hidden />
                <h3 id="vault-doc-detail-title" className="font-display font-semibold text-lg truncate">{openDoc.label}</h3>
              </div>
              <button onClick={() => setOpenDoc(null)} aria-label="Close"
                      className="vh-focusable w-9 h-9 rounded-full hover:bg-[var(--vh-bg-muted)] text-[var(--vh-text-muted)] flex items-center justify-center"
                      data-testid="vault-doc-flyout-close">
                <X className="w-5 h-5" />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm">
              <Detail label="Original filename" value={openDoc.original_filename} />
              <Detail label="MIME type" value={openDoc.mime_type} />
              <Detail label="Size" value={humanSize(openDoc.size_bytes)} />
              <Detail label="Status" value={openDoc.status} />
              <Detail label="Uploaded" value={openDoc.uploaded_at?.replace('T', ' ').slice(0, 16) + ' UTC'} />

              {openDoc.mime_type?.startsWith('image/') && (
                <div className="border border-[var(--vh-border)] rounded-xl overflow-hidden mt-3">
                  <img src={downloadUrl(openDoc)} alt={openDoc.label} className="w-full h-auto" />
                </div>
              )}
              {openDoc.mime_type === 'application/pdf' && (
                <iframe
                  title={openDoc.label}
                  src={downloadUrl(openDoc)}
                  className="w-full h-96 mt-3 border border-[var(--vh-border)] rounded-xl"
                />
              )}
            </div>
            <footer className="px-5 py-3 border-t border-[var(--vh-border)] flex gap-2 justify-end">
              <a href={downloadUrl(openDoc)} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 px-4 py-2 border border-[var(--vh-border)] hover:bg-[var(--vh-bg-muted)] text-sm rounded-full"
                 data-testid="vault-doc-flyout-download">
                <Download className="w-4 h-4" /> Download
              </a>
              <button onClick={() => remove(openDoc)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--vh-accent)] hover:bg-[var(--vh-accent-hover)] text-white text-sm rounded-full"
                      data-testid="vault-doc-flyout-delete">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </footer>
          </aside>
        </>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--vh-text-muted)]">{label}</p>
      <p className="text-sm break-words">{value || <span className="text-[var(--vh-text-muted)] italic">—</span>}</p>
    </div>
  );
}
